import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { useStoreManagementStore } from "@/store/storeManagement";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EmployeePersonalFields } from "./employees/EmployeePersonalFields";
import { EmployeeAddressFields } from "./employees/EmployeeAddressFields";
import { employeeSchema, EmployeeFormValues } from "./employees/employeeSchema";

interface AddEmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
}

export function AddEmployeeDialog({
  open,
  onOpenChange,
  storeId,
}: AddEmployeeDialogProps) {
  const { addEmployee } = useStoreManagementStore();
  const { toast } = useToast();

  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      role: "staff",
      street: "",
      streetNumber: "",
      city: "",
      postcode: "",
    },
  });

  const onSubmit = async (data: EmployeeFormValues) => {
    try {
      // Calculate the full address from individual components
      const addressParts = [
        data.streetNumber,
        data.street,
        data.city,
        data.postcode,
      ].filter(Boolean);

      const address =
        addressParts.length > 0 ? addressParts.join(", ") : undefined;

      await addEmployee({
        storeId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        role: data.role,
        address, // Computed address from components
        street: data.street,
        streetNumber: data.streetNumber,
        city: data.city,
        postcode: data.postcode,
      });

      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${data.email}`,
      });
      form.reset();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send invitation.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite Employee to Store</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 py-4"
          >
            <EmployeePersonalFields control={form.control} />
            <EmployeeAddressFields control={form.control} />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting
                  ? "Sending Invitation..."
                  : "Send Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
